import axios from 'axios';
import { OperationsResponse, DatabaseStatus, DatabaseConfig, OperationsStats, DashboardFilters, BankAccount, Budget, BudgetWithConsumption, BudgetFormData, Category } from '../types';
import { API_URL } from '../config';
import { Workflow, WorkflowBody, Execution, ValidationPayload, ImportApercu } from '../types/workflow';
import { ModeleImport, ModeleImportBody, ConfigImport, EssaiModele } from '../types/importModel';

// Interface pour les paramètres de filtre API
interface FilterParams {
  userId?: string;
  accountId?: string;
  dateFrom?: string;
  dateTo?: string;
}

const API_BASE_URL = API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // SEC-H1 : le JWT vit dans un cookie httpOnly posé par l'API. Il faut donc que le
  // navigateur joigne les cookies aux requêtes cross-origin (le backend répond
  // Access-Control-Allow-Credentials via son allowlist CORS).
  withCredentials: true,
});

// SEC-H1 : jeton CSRF gardé UNIQUEMENT en mémoire.
// Le persister (localStorage) rouvrirait la porte qu'on vient de fermer : il redeviendrait
// lisible par un XSS, qui pourrait alors accompagner le cookie ambiant d'un CSRF valide.
// Au rechargement de page, il est re-obtenu via GET /api/auth/me.
let csrfToken: string | null = null;

export const setCsrfToken = (value: string | null) => {
  csrfToken = value;
};

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (csrfToken && MUTATING_METHODS.includes(method)) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// Intercepteur pour les erreurs avec gestion de l'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/register');

    // Session expirée (401 hors tentative de login/register) :
    // BUG-H10 — on nettoie et on NOTIFIE l'app via un événement, au lieu de
    // window.location.reload() qui détruisait brutalement l'état (formulaires en cours).
    // 403 INVALID_CSRF est traité pareil : un jeton CSRF périmé signifie que la session
    // sous-jacente ne vaut plus rien, il faut repasser par le login.
    const isCsrfFailure = status === 403 && error.response?.data?.code === 'INVALID_CSRF';

    if ((status === 401 || isCsrfFailure) && !isAuthEndpoint) {
      // Le cookie d'auth est httpOnly : seul le serveur peut l'effacer (POST /logout).
      // Ici on se contente d'oublier le jeton CSRF et de notifier l'app.
      csrfToken = null;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Instance axios partagée (BUG-H8) : à utiliser partout (contexts, adminApi)
// pour bénéficier de l'ajout du token + de la gestion 401 centralisée.
export { api };

export const operationsApi = {
  // Liste des opérations avec pagination
  getOperations: async (params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'ASC' | 'DESC';
  }): Promise<OperationsResponse> => {
    const response = await api.get('/api/operations', { params });
    return response.data;
  },

  // Récupérer les opérations avec filtres
  getOperationsWithFilters: async (filters: {
    user_id?: number | null;
    account_id?: number | null;
    date_from?: string;
    date_to?: string;
  }): Promise<{ operations: any[] }> => {
    const params: any = {};
    if (filters.account_id) params.compte_id = filters.account_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    params.limit = 10000; // Limite haute pour récupérer toutes les opérations de la période

    const response = await api.get('/api/operations', { params });
    return { operations: response.data.operations || [] };
  },

  // Créer une nouvelle opération
  createOperation: async (operationData: {
    montant: number;
    date_op: string;
    operation: string;
    note?: string;
    id_categorie?: number;
    id_compte?: number;
  }) => {
    const response = await api.post('/api/operations', operationData);
    return response.data;
  },

  // Statistiques comparatives (mois actuel vs précédent)
  getComparativeStats: async () => {
    const response = await api.get('/api/operations/stats/comparative');
    return response.data;
  },

  // Top 3 des catégories du mois courant
  getTopCategoriesCurrentMonth: async (filters?: FilterParams) => {
    const params = new URLSearchParams();
    if (filters?.userId) params.append('user_id', filters.userId);
    if (filters?.accountId) params.append('account_id', filters.accountId);
    if (filters?.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters?.dateTo) params.append('date_to', filters.dateTo);
    
    const response = await api.get(`/api/operations/stats/top-categories-current-month?${params}`);
    return response.data;
  },

  // Tendances de dépenses par catégorie
  getSpendingTrends: async (filters?: FilterParams) => {
    const params = new URLSearchParams();
    if (filters?.userId) params.append('user_id', filters.userId);
    if (filters?.accountId) params.append('account_id', filters.accountId);
    if (filters?.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters?.dateTo) params.append('date_to', filters.dateTo);
    
    const response = await api.get(`/api/operations/stats/spending-trends?${params}`);
    return response.data;
  },

  // Paramètres utilisateur
  getUserSettings: async () => {
    const response = await api.get('/api/operations/stats/user-settings');
    return response.data;
  },

  // Sauvegarder paramètres utilisateur
  saveUserSettings: async (settings: { salary_day: number; salary_frequency: string }) => {
    const response = await api.post('/api/operations/stats/user-settings', settings);
    return response.data;
  },

  // Import CSV
  importCsv: async (formData: FormData) => {
    const response = await api.post('/api/operations/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Le timeout global de 10 s de l'instance est calibré pour des appels JSON.
      // Un import CSV (upload + parsing + insertions transactionnelles) le dépasse
      // largement sur un gros fichier : on le neutralise pour cette requête.
      timeout: 0,
    });
    return response.data;
  },

  // Aperçu de l'import CSV (mêmes champs que importCsv), sans rien écrire
  previewCsv: async (formData: FormData): Promise<ImportApercu & { success: boolean; compteId: number | string }> => {
    const response = await api.post('/api/operations/import-csv/apercu', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
    });
    return response.data;
  },

  // Split operation
  splitOperation: async (operationId: number, amounts: number[]) => {
    const response = await api.post(`/api/operations/${operationId}/split`, {
      amounts
    });
    return response.data;
  },

  // Catégorisation automatique
  getUncategorizedOperations: async (accountId?: string) => {
    const params: any = {};
    if (accountId) {
      params.account_id = accountId;
    }
    const response = await api.get('/api/operations/uncategorized', { params });
    return response.data;
  },

  previewCategorization: async (operationIds: number[]) => {
    const response = await api.post('/api/operations/preview-categorization', { operationIds });
    return response.data;
  },

  applyCategorization: async (updates: { operation_id: number; id_categorie?: number; tag_ids?: number[] }[]) => {
    const response = await api.post('/api/operations/apply-categorization', { updates });
    return response.data;
  },

  // Chart data endpoints
  getBalanceEvolution: async (params?: { startDate?: string; endDate?: string; period?: string; user_id?: string; account_id?: string }) => {
    const response = await api.get('/api/operations/charts/balance-evolution', { params });
    return response.data;
  },

  getMonthlyExpenses: async (params?: { startDate?: string; endDate?: string; period?: string; user_id?: string; account_id?: string }) => {
    const response = await api.get('/api/operations/charts/monthly-expenses', { params });
    return response.data;
  },

  getMonthlyIncome: async (params?: { startDate?: string; endDate?: string; period?: string; user_id?: string; account_id?: string }) => {
    const response = await api.get('/api/operations/charts/monthly-income', { params });
    return response.data;
  },

  getMonthEndBalance: async (params?: { startDate?: string; endDate?: string; period?: string; user_id?: string; account_id?: string }) => {
    const response = await api.get('/api/operations/charts/month-end-balance', { params });
    return response.data;
  },

  getTopExpenses: async (params?: { startDate?: string; endDate?: string; period?: string; user_id?: string; account_id?: string }) => {
    const response = await api.get('/api/operations/charts/top-expenses', { params });
    return response.data;
  },

  getMonthlyBalance: async (params?: { startDate?: string; endDate?: string; period?: string; user_id?: string; account_id?: string }) => {
    const response = await api.get('/api/operations/charts/monthly-balance', { params });
    return response.data;
  },

  // Update operation
  updateOperation: async (id: number, data: {
    operation: string;
    note?: string | null;
    montant: number;
    date_op: string;
    id_categorie?: number | null;
  }) => {
    const response = await api.put(`/api/operations/${id}`, data);
    return response.data;
  },

  // Delete operation
  deleteOperation: async (id: number) => {
    const response = await api.delete(`/api/operations/${id}`);
    return response.data;
  },

  // Bulk delete operations
  bulkDelete: async (operationIds: number[]) => {
    const response = await api.post('/api/operations/bulk-delete', { operationIds });
    return response.data;
  },

  // Get categories for bulk operations
  getCategories: async () => {
    const response = await api.get('/api/config/categories');
    return response.data;
  },

  // === NOUVELLES MÉTHODES MULTI-UTILISATEURS ET COMPTES BANCAIRES ===
  
  // Statistiques avec filtres avancés (utilisateur, compte, dates)
  getStatsWithFilters: async (filters: DashboardFilters): Promise<OperationsStats> => {
    const response = await api.get('/api/operations/stats/filtered', { params: filters });
    return response.data;
  },

  // Comptes bancaires de l'utilisateur connecté
  getBankAccounts: async (): Promise<{ success: boolean; accounts: BankAccount[] }> => {
    const response = await api.get('/api/operations/accounts');
    return response.data;
  },
};

export const keywordsApi = {
  // Get all keywords
  getKeywords: async () => {
    const response = await api.get('/api/keywords');
    return response.data;
  },

  // Create a new keyword
  createKeyword: async (data: { mot_cle: string; id_categorie: number | null; tag_ids: number[] }) => {
    const response = await api.post('/api/keywords', data);
    return response.data;
  },

  // Update a keyword
  updateKeyword: async (id: number, data: { mot_cle: string; id_categorie: number | null; tag_ids: number[] }) => {
    const response = await api.put(`/api/keywords/${id}`, data);
    return response.data;
  },

  // Delete a keyword
  deleteKeyword: async (id: number) => {
    const response = await api.delete(`/api/keywords/${id}`);
    return response.data;
  },
};

export const savedSearchesApi = {
  // Get all saved searches for the current user
  getSavedSearches: async () => {
    const response = await api.get('/api/saved-searches');
    return response.data;
  },

  // Get default search
  getDefaultSearch: async () => {
    const response = await api.get('/api/saved-searches/default/query');
    return response.data;
  },

  // Create a new saved search
  createSavedSearch: async (data: {
    name: string;
    description?: string;
    search_query: any;
    is_default?: boolean;
  }) => {
    const response = await api.post('/api/saved-searches', data);
    return response.data;
  },

  // Update a saved search
  updateSavedSearch: async (id: number, data: {
    name?: string;
    description?: string;
    search_query?: any;
    is_default?: boolean;
  }) => {
    const response = await api.put(`/api/saved-searches/${id}`, data);
    return response.data;
  },

  // Delete a saved search
  deleteSavedSearch: async (id: number) => {
    const response = await api.delete(`/api/saved-searches/${id}`);
    return response.data;
  },
};

export const configApi = {
  // Test de connexion à la base actuelle
  getDatabaseStatus: async (): Promise<DatabaseStatus> => {
    const response = await api.get('/api/config/database');
    return response.data;
  },

  // Test avec nouvelles informations de connexion
  testDatabaseConnection: async (config: DatabaseConfig): Promise<DatabaseStatus> => {
    const response = await api.post('/api/config/database', config);
    return response.data;
  },

  // Liste des catégories
  getCategories: async () => {
    const response = await api.get('/api/config/categories');
    return response.data;
  },

  // Créer une catégorie (racine ou enfant si parent_id est fourni)
  createCategory: async (categoryData: { nom: string; parent_id?: number | null; type?: 'revenu' | null }) => {
    const response = await api.post('/api/config/categories', categoryData);
    return response.data;
  },

  // Modifier une catégorie : PUT accepte un sous-ensemble quelconque des champs
  // (renommer, déplacer, changer le type, ou combiner) — `nom` est donc optionnel
  // ici contrairement à la création. config.js applique COALESCE($1, nom) pour le
  // nom, et un couple (présent/valeur) séparé pour parent_id et type : un champ
  // absent du body laisse la colonne inchangée, un `null` explicite l'efface
  // (utile pour ramener une catégorie à la racine ou lui retirer son type revenu).
  updateCategory: async (id: number, categoryData: { nom?: string; parent_id?: number | null; type?: 'revenu' | null }) => {
    const response = await api.put(`/api/config/categories/${id}`, categoryData);
    return response.data;
  },

  // Supprimer une catégorie
  deleteCategory: async (id: number) => {
    const response = await api.delete(`/api/config/categories/${id}`);
    return response.data;
  },
};

export const budgetsApi = {
  // Récupérer tous les budgets
  getBudgets: async (includeInactive: boolean = false): Promise<{ success: boolean; budgets: Budget[] }> => {
    const response = await api.get('/api/budgets', {
      params: { includeInactive: includeInactive.toString() }
    });
    return response.data;
  },

  // Récupérer les budgets avec consommation
  getBudgetsWithConsumption: async (
    includeInactive: boolean = false,
    accountId?: number,
    dateFrom?: string,
    dateTo?: string
  ): Promise<{ success: boolean; budgets: BudgetWithConsumption[] }> => {
    const params: any = { includeInactive: includeInactive.toString() };

    if (accountId) {
      params.account_id = accountId;
    }
    if (dateFrom) {
      params.date_from = dateFrom;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }

    const response = await api.get('/api/budgets/with-consumption', { params });
    return response.data;
  },

  // Créer un nouveau budget
  createBudget: async (budgetData: BudgetFormData): Promise<{ success: boolean; budget: Budget; message: string }> => {
    const response = await api.post('/api/budgets', budgetData);
    return response.data;
  },

  // Mettre à jour un budget
  updateBudget: async (id: number, budgetData: Partial<BudgetFormData> & { is_active?: boolean }): Promise<{ success: boolean; budget: Budget; message: string }> => {
    const response = await api.put(`/api/budgets/${id}`, budgetData);
    return response.data;
  },

  // Cloner un budget sur la période suivante
  cloneBudget: async (id: number): Promise<{ success: boolean; budget: Budget; message: string }> => {
    const response = await api.post(`/api/budgets/${id}/clone`);
    return response.data;
  },

  // Supprimer un budget (soft delete par défaut)
  deleteBudget: async (id: number, hard: boolean = false): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/budgets/${id}`, {
      params: { hard: hard.toString() }
    });
    return response.data;
  },

  // Récupérer les alertes actives
  getCurrentAlerts: async (dateFrom?: string, dateTo?: string, accountId?: number): Promise<{ success: boolean; alerts: BudgetWithConsumption[] }> => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (accountId) params.append('account_id', accountId.toString());

    const response = await api.get('/api/budgets/alerts/current', {
      params: params.toString() ? params : undefined
    });
    return response.data;
  },

  // Récupérer le résumé des budgets
  getSummary: async (dateFrom?: string, dateTo?: string, accountId?: number): Promise<{ success: boolean; summary: any }> => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (accountId) params.append('account_id', accountId.toString());

    const response = await api.get('/api/budgets/summary', {
      params: params.toString() ? params : undefined
    });
    return response.data;
  },

  // Récupérer l'évolution de la consommation sur plusieurs mois
  getConsumptionEvolution: async (dateFrom?: string, dateTo?: string, accountId?: number): Promise<{ success: boolean; evolution: any[] }> => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (accountId) params.append('account_id', accountId.toString());

    const response = await api.get('/api/budgets/consumption-evolution', {
      params: params.toString() ? params : undefined
    });
    return response.data;
  },

  // Récupérer les catégories disponibles pour cibler un budget.
  // budgets.js (GET /categories/available) ne renvoie que
  // `{ id, nom, parent_id }` par ligne — pas le nœud `Category` complet (pas de
  // `type`/`profondeur`/`chemin`/compteurs), et plus de clé `sousCategories` :
  // la notion de sous-catégorie distincte a disparu avec l'arborescence.
  getAvailableCategories: async (): Promise<{ success: boolean; categories: Pick<Category, 'id' | 'nom' | 'parent_id'>[] }> => {
    const response = await api.get('/api/budgets/categories/available');
    return response.data;
  },
};

// API des tags
export const tagsApi = {
  // Récupérer tous les tags de l'utilisateur
  getTags: async (): Promise<{ success: boolean; tags: any[] }> => {
    const response = await api.get('/api/tags');
    return response.data;
  },

  // Créer un nouveau tag
  createTag: async (data: { nom: string; couleur?: string; description?: string }): Promise<{ success: boolean; tag: any }> => {
    const response = await api.post('/api/tags', data);
    return response.data;
  },

  // Mettre à jour un tag
  updateTag: async (id: number, data: { nom?: string; couleur?: string; description?: string }): Promise<{ success: boolean; tag: any }> => {
    const response = await api.put(`/api/tags/${id}`, data);
    return response.data;
  },

  // Supprimer un tag
  deleteTag: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/tags/${id}`);
    return response.data;
  },

  // Ajouter un tag à une opération
  addTagToOperation: async (tagId: number, operationId: number): Promise<{ success: boolean; added: boolean }> => {
    const response = await api.post(`/api/tags/${tagId}/operations/${operationId}`);
    return response.data;
  },

  // Retirer un tag d'une opération
  removeTagFromOperation: async (tagId: number, operationId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/tags/${tagId}/operations/${operationId}`);
    return response.data;
  },

  // Récupérer les statistiques globales par tags
  getTagStatistics: async (filters?: { date_from?: string; date_to?: string; account_id?: number }): Promise<{ success: boolean; statistics: any[] }> => {
    const params = new URLSearchParams();
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.account_id) params.append('account_id', filters.account_id.toString());

    const response = await api.get('/api/tags/statistics/summary', {
      params: params.toString() ? params : undefined
    });
    return response.data;
  },

  // Récupérer les statistiques par tag ventilées par mois (mois au format YYYY-MM)
  getTagMonthlyStatistics: async (filters?: { date_from?: string; date_to?: string; account_id?: number }): Promise<{ success: boolean; statistics: any[] }> => {
    const params = new URLSearchParams();
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.account_id) params.append('account_id', filters.account_id.toString());

    const response = await api.get('/api/tags/statistics/monthly', {
      params: params.toString() ? params : undefined
    });
    return response.data;
  },
};

export const auditApi = {
  // Récupérer les logs d'audit avec pagination et filtres
  getLogs: async (params?: {
    page?: number;
    limit?: number;
    table_name?: string;
    action?: string;
    user_id?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
  }): Promise<{ success: boolean; logs: any[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.table_name) queryParams.append('table_name', params.table_name);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.user_id) queryParams.append('user_id', params.user_id.toString());
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get('/api/audit', {
      params: queryParams.toString() ? queryParams : undefined
    });
    return response.data;
  },

  // Récupérer les statistiques d'audit
  getStats: async (params?: { date_from?: string; date_to?: string }): Promise<{ success: boolean; stats: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);

    const response = await api.get('/api/audit/stats', {
      params: queryParams.toString() ? queryParams : undefined
    });
    return response.data;
  },
};

// Workflows d'import et de catégorisation
export const workflowsApi = {
  list: async (): Promise<Workflow[]> => {
    const response = await api.get('/api/workflows');
    return response.data;
  },

  get: async (id: number): Promise<Workflow> => {
    const response = await api.get(`/api/workflows/${id}`);
    return response.data;
  },

  create: async (body: WorkflowBody): Promise<Workflow> => {
    const response = await api.post('/api/workflows', body);
    return response.data;
  },

  update: async (id: number, body: WorkflowBody): Promise<Workflow> => {
    const response = await api.put(`/api/workflows/${id}`, body);
    return response.data;
  },

  // 204 sans corps
  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/workflows/${id}`);
  },

  // Lance une exécution sur le CSV (champ « csvFile », comme importCsv)
  start: async (id: number, file: File): Promise<Execution> => {
    const formData = new FormData();
    formData.append('csvFile', file);
    const response = await api.post(`/api/workflows/${id}/executions`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Comme importCsv : l'upload et les étapes automatiques dépassent le timeout global de 10 s
      timeout: 0,
    });
    return response.data;
  },

  getExecution: async (id: number): Promise<Execution> => {
    const response = await api.get(`/api/workflows/executions/${id}`);
    return response.data;
  },

  // Les étapes reviennent avec resultat: null (lire l'exécution pour l'aperçu)
  listPending: async (): Promise<Execution[]> => {
    const response = await api.get('/api/workflows/executions/en-attente');
    return response.data;
  },

  validate: async (id: number, payload: ValidationPayload): Promise<Execution> => {
    // Valider un import manuel lance l'import réel puis les étapes automatiques suivantes dans la même
    // requête : le timeout global de 10 s ne suffit pas (comme start).
    const response = await api.post(`/api/workflows/executions/${id}/valider`, payload, { timeout: 0 });
    return response.data;
  },

  cancel: async (id: number): Promise<Execution> => {
    const response = await api.post(`/api/workflows/executions/${id}/annuler`);
    return response.data;
  },
};

// Modèles d'import CSV de l'utilisateur
export const importModelsApi = {
  list: async (): Promise<ModeleImport[]> => {
    const response = await api.get('/api/import-models');
    return response.data;
  },

  create: async (body: ModeleImportBody): Promise<ModeleImport> => {
    const response = await api.post('/api/import-models', body);
    return response.data;
  },

  update: async (id: number, body: ModeleImportBody): Promise<ModeleImport> => {
    const response = await api.put(`/api/import-models/${id}`, body);
    return response.data;
  },

  // 204 sans corps
  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/import-models/${id}`);
  },

  // Essai d'une configuration sur un fichier d'exemple ; sans config, le serveur la détecte
  essai: async (file: File, config?: ConfigImport): Promise<EssaiModele> => {
    const formData = new FormData();
    formData.append('csvFile', file);
    if (config) formData.append('config', JSON.stringify(config));
    const response = await api.post('/api/import-models/essai', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
    });
    return response.data;
  },
};

export default api;
